/**
 * Pure parsing + shaping helpers for the `run_cypher` tool result. No React,
 * no IO — the controller (RunCypherToolUI) and the presentational view both
 * consume these.
 */

export interface RunCypherArgs {
  cypher: string
  parameters?: Record<string, unknown>
  row_limit?: number
  timeout_seconds?: number
}

/**
 * Backend payload: JSON string of
 *   { query, params, status: 'ok'|'error', count?, rows?, error?, elapsed_ms? }
 * (may arrive as a pre-parsed object when history replays.)
 */
export type BackendPayload = {
  query?: string
  params?: Record<string, unknown>
  status?: 'ok' | 'error'
  count?: number
  rows?: Record<string, unknown>[]
  error?: string
  elapsed_ms?: number
}

export type ParsedResult =
  | { kind: 'rows'; count: number; rows: Record<string, unknown>[]; elapsedMs?: number }
  | { kind: 'empty'; elapsedMs?: number }
  | { kind: 'error'; message: string }
  | { kind: 'raw'; text: string }

export function parseResult(raw: unknown): ParsedResult | null {
  if (raw === undefined || raw === null) return null

  let obj: BackendPayload | null = null
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw) as BackendPayload
    } catch {
      return { kind: 'raw', text: raw }
    }
  } else if (typeof raw === 'object') {
    obj = raw as BackendPayload
  }

  if (!obj) return { kind: 'raw', text: String(raw) }

  if (obj.status === 'error') {
    return { kind: 'error', message: obj.error ?? 'Unknown error' }
  }

  const rows = Array.isArray(obj.rows) ? obj.rows : []
  if (rows.length === 0) return { kind: 'empty', elapsedMs: obj.elapsed_ms }
  return { kind: 'rows', count: obj.count ?? rows.length, rows, elapsedMs: obj.elapsed_ms }
}

export function columnsFrom(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>()
  for (const row of rows) for (const k of Object.keys(row)) seen.add(k)
  return [...seen]
}

export function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (Array.isArray(v)) return `[${v.length}]`
  if (typeof v === 'object') return `{${Object.keys(v as object).length} keys}`
  return String(v)
}

export function isNumericColumn(rows: Record<string, unknown>[], col: string): boolean {
  let seen = false
  for (const r of rows) {
    const v = r[col]
    if (v === null || v === undefined) continue
    if (typeof v !== 'number') return false
    seen = true
  }
  return seen
}

export function shortId(id: string): string {
  if (id.length <= 12) return id
  return `${id.slice(0, 6)}…${id.slice(-4)}`
}

/** Neo4j element ids look like `4:2fac9804-fac1-4fbe-...:123`. */
const ELEMENT_ID_RE = /^\d+:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

export function isElementId(v: unknown): v is string {
  return typeof v === 'string' && ELEMENT_ID_RE.test(v)
}

/**
 * Scan every cell for Neo4j element ids — `run_cypher` returns arbitrary
 * shapes (e.g. `RETURN b.element_id, b.cell_id`) so we can't rely on a single
 * column name. Dedup-preserve order. Empty when no rows look like node
 * references; the action footer then hides itself.
 */
export function extractEntityIds(parsed: ParsedResult | null): string[] {
  if (parsed?.kind !== 'rows') return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const row of parsed.rows) {
    for (const v of Object.values(row)) {
      if (isElementId(v) && !seen.has(v)) {
        seen.add(v)
        out.push(v)
      }
    }
  }
  return out
}
